import React, { useCallback } from 'react'
import {
  Upload, Button, List, Space, Empty, Spin, Popconfirm, Typography, Image,
} from 'antd'
import {
  UploadOutlined, DeleteOutlined, FileOutlined,
  FilePdfOutlined, FileExcelOutlined, FileWordOutlined, FileImageOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import useDocs from '../../../hooks/useDocs'
import { BACKEND_URL } from '../../../services/backendURL'
import { BRAND } from '../constants'

const { Text } = Typography

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif']

function getFileIcon(filename) {
  if (!filename) return <FileOutlined />
  const ext = filename.split('.').pop()?.toLowerCase()
  if (IMAGE_EXTS.includes(ext)) return <FileImageOutlined style={{ color: BRAND }} />
  if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
  if (['xls', 'xlsx'].includes(ext)) return <FileExcelOutlined style={{ color: '#52c41a' }} />
  if (['doc', 'docx'].includes(ext)) return <FileWordOutlined style={{ color: BRAND }} />
  return <FileOutlined />
}

function isImage(filename) {
  if (!filename) return false
  const ext = filename.split('.').pop()?.toLowerCase()
  return IMAGE_EXTS.includes(ext)
}

function getDocUrl(doc) {
  const url = doc.doc_file || doc.file || doc.url
  if (!url) return '#'
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`
}

export default function Documents({ membertaskId, disabled }) {
  const { docs, loading, uploading, addDoc, deleteDoc } = useDocs(membertaskId)

  const handleUpload = useCallback(async (info) => {
    const file = info.file
    await addDoc(file)
  }, [addDoc])

  return (
    <div>
      {!disabled && (
        <div style={{ marginBottom: 16 }}>
          <Upload
            beforeUpload={() => false}
            customRequest={handleUpload}
            showUploadList={false}
            accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx"
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploading}
              type="primary"
              style={{ backgroundColor: BRAND, borderColor: BRAND }}
              aria-label="Ngarko dokument"
            >
              Ngarko dokument
            </Button>
          </Upload>
        </div>
      )}

      <Spin spinning={loading}>
        {docs.length === 0 && !loading ? (
          <Empty description="Nuk ka dokumente" />
        ) : (
          <List
            dataSource={docs}
            renderItem={(doc) => {
              const url = getDocUrl(doc)
              const filename = doc.doc_file?.split('/').pop() || doc.description || 'Dokument'

              return (
                <List.Item
                  actions={[
                    <a
                      key="download"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: BRAND }}
                      aria-label="Shkarko"
                    >
                      <DownloadOutlined /> Shkarko
                    </a>,
                    ...(!disabled && doc.is_mine
                      ? [
                          <Popconfirm
                            key="delete"
                            title="Fshi dokumentin?"
                            onConfirm={() => deleteDoc(doc.id)}
                            okText="Po"
                            cancelText="Jo"
                          >
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              aria-label="Fshi dokumentin"
                            />
                          </Popconfirm>,
                        ]
                      : []),
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      isImage(filename)
                        ? <Image width={48} height={48} src={url} style={{ objectFit: 'cover', borderRadius: 4 }} preview alt={filename} />
                        : <div style={{ width: 48, textAlign: 'center', fontSize: 24 }}>{getFileIcon(filename)}</div>
                    }
                    title={<Text>{doc.description || filename}</Text>}
                    description={
                      <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {doc.member ? `${doc.member.name} ${doc.member.surname}` : ''}
                        </Text>
                        {doc.create_date && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {moment(doc.create_date).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Spin>
    </div>
  )
}
